import type {ConsultaDeNaviera, ResultadoDeRevision} from '@cnc/contracts';
import {useState} from 'react';
import {Link} from 'react-router';
import {ErrorDeApi} from '../api/cliente';
import {useConsultasDeNaviera, useEstadoDeNavieras, useMarcarRevisada, useRevisarNavieras} from '../api/navieras';
import {AYUDA_NAVIERAS as A} from '../componentes/ayuda-exportaciones';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {PestanasSeguimiento} from '../componentes/pestanas-seguimiento';
import {usePermisos} from '../componentes/sesion-contexto';
import {tr} from '../i18n/idioma';
import {fecha, fechaHora, numero, textoOGuion} from '../utilidades/formato';

// Conexion con las navieras: estado de cada conexion, revision manual y la
// lista de cambios de ETD, ETA, buque y ubicacion que trajo cada consulta.
export function PaginaNavieras() {
  const permisos = usePermisos();
  const estado = useEstadoDeNavieras();
  const [filtro, setFiltro] = useState<'pendientes' | 'cambios' | 'todas'>('pendientes');
  const consultas = useConsultasDeNaviera({
    soloPendientes: filtro === 'pendientes',
    soloConCambios: filtro === 'cambios',
  });
  const revisar = useRevisarNavieras();
  const marcar = useMarcarRevisada();
  const [resultado, setResultado] = useState<ResultadoDeRevision | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);

  const revisarAhora = async () => {
    setMensaje(null);
    try {
      setResultado(await revisar.mutateAsync());
    } catch (error) {
      setMensaje(error instanceof ErrorDeApi ? error.message : tr('No se pudo revisar.', 'Could not run the check.'));
    }
  };

  return (
    <section className="max-w-6xl">
      <PestanasSeguimiento />
      <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-verde-oscuro">{tr('Navieras', 'Shipping lines')}</h1>
          <p className="text-sm text-apagado">
            {tr(
              'Revisión de los bookings de las exportaciones en curso: si cambian el ETD, la ETA, el buque o la ubicación, se actualizan y quedan aquí para revisar.',
              'Review of the bookings of exports in progress: if the ETD, ETA, vessel or location change, they are updated and listed here for review.',
            )}
          </p>
        </div>
        {permisos.editar && estado.data?.conectores.some(c => c.configurado) && (
          <button
            type="button"
            onClick={() => void revisarAhora()}
            disabled={revisar.isPending}
            className="rounded-md bg-verde px-4 py-2 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
          >
            {revisar.isPending ? tr('Consultando…', 'Checking…') : tr('↻ Revisar ahora', '↻ Check now')}
          </button>
        )}
      </div>
      {mensaje && <p className="mt-2 text-sm text-alerta">{mensaje}</p>}

      {/* Por ahora las navieras se actualizan con el rastreador que imprime los
          PDF de sus paginas (decision de Daniel del 7/10/2026), no con claves de API. */}
      <div
        className="mt-4 rounded-lg border border-verde/40 bg-verde-claro/50 p-4 text-sm"
        data-prueba="navieras-rastreador"
      >
        <div className="font-medium text-verde-oscuro">
          {tr('Cómo se actualizan hoy las navieras', 'How shipping lines are updated today')}
        </div>
        <ol className="mt-1 list-decimal space-y-0.5 pl-5">
          <li>
            {tr(
              'En Exportaciones, pulsa "🚢 Actualizar desde navieras" y descarga la lista de bookings en curso.',
              'In Exports, click "🚢 Update from shipping lines" and download the list of bookings in progress.',
            )}
          </li>
          <li>
            {tr(
              'Corre el rastreador: abre la página de cada naviera e imprime el PDF de cada booking.',
              "Run the tracker: it opens each shipping line's page and prints the PDF of each booking.",
            )}
          </li>
          <li>
            {tr(
              'Sube los PDF y el Excel que deja el rastreador; la app muestra qué cambia (ETD, ETA, llegada) para revisar antes de aplicar.',
              'Upload the PDFs and the Excel the tracker leaves; the app shows what changes (ETD, ETA, arrival) to review before applying.',
            )}
          </li>
        </ol>
        <div className="mt-2 flex flex-wrap gap-x-4">
          <Link to="/seguimiento" className="text-verde hover:underline">
            {tr('Ir a Exportaciones →', 'Go to Exports →')}
          </Link>
          <Link to="/seguimiento/reservas" className="text-verde hover:underline">
            {tr('Revisar las reservas →', 'Review the bookings →')}
          </Link>
        </div>
      </div>

      {estado.data && (
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {estado.data.conectores.map(c => (
            <div key={c.codigo} className="rounded-lg border border-linea bg-white p-4">
              <div className="flex items-center justify-between">
                <div className="font-medium">{c.nombre}</div>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs ${c.configurado ? 'bg-verde-claro text-verde-oscuro' : 'bg-dorado-claro text-tinta'}`}
                >
                  {c.configurado ? tr('Conectada', 'Connected') : tr('Con el rastreador', 'With the tracker')}
                  <NotaDeAyuda texto={A.conexion} />
                </span>
              </div>
              <div className="mt-2 text-sm text-apagado">
                {tr(
                  `${numero(c.exportacionesEnCurso)} exportaciones en curso`,
                  `${numero(c.exportacionesEnCurso)} exports in progress`,
                )}
                <NotaDeAyuda texto={A.enCurso} />
                <br />
                {tr('Última consulta:', 'Last check:')}{' '}
                {c.ultimaConsulta ? fechaHora(c.ultimaConsulta) : tr('nunca', 'never')}
                <NotaDeAyuda texto={A.ultimaConsulta} />
              </div>
            </div>
          ))}
          <div className="rounded-lg border border-linea bg-papel p-4 text-sm md:col-span-3">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-apagado">
              <span>
                {tr('Revisión automática:', 'Automatic check:')}{' '}
                {estado.data.activo
                  ? tr(
                      `todos los días a las ${horaDeCron(estado.data.horaDeRevision)} (hora de Bogotá)`,
                      `every day at ${horaDeCron(estado.data.horaDeRevision)} (Bogotá time)`,
                    )
                  : tr('apagada', 'off')}
                <NotaDeAyuda texto={A.automatica} />
              </span>
              <span>
                {tr('Última revisión:', 'Last check:')}{' '}
                {estado.data.ultimaRevision ? fechaHora(estado.data.ultimaRevision) : tr('ninguna todavía', 'none yet')}
                <NotaDeAyuda texto={A.ultimaRevision} />
              </span>
              <span>
                {tr('Cambios por revisar:', 'Changes to review:')} {numero(estado.data.cambiosPorRevisar)}
                <NotaDeAyuda texto={A.porRevisar} />
              </span>
            </div>
            {estado.data.sinConexion.length > 0 && (
              <div className="mt-2">
                {tr(
                  'Navieras sin conexión con la app (se siguen a mano):',
                  'Shipping lines not connected to the app (tracked manually):',
                )}{' '}
                {estado.data.sinConexion.map(s => `${s.naviera} (${numero(s.exportacionesEnCurso)})`).join(', ')}.
                <NotaDeAyuda texto={A.sinConexion} />
              </div>
            )}
          </div>
        </div>
      )}

      {resultado && (
        <div className="mt-4 rounded-lg border border-verde/40 bg-verde-claro p-4 text-sm">
          <div className="font-medium text-verde-oscuro">{tr('Revisión terminada', 'Check finished')}</div>
          <div className="mt-1">
            {tr(
              `${numero(resultado.revisadas)} bookings consultados: ${numero(resultado.conCambios)} con cambios, ${numero(resultado.sinCambios)} sin cambios, ${numero(resultado.noEncontradas)} no encontrados por la naviera, ${numero(resultado.errores)} con error. ${numero(resultado.omitidas)} exportaciones no se consultaron`,
              `${numero(resultado.revisadas)} bookings checked: ${numero(resultado.conCambios)} with changes, ${numero(resultado.sinCambios)} without changes, ${numero(resultado.noEncontradas)} not found by the shipping line, ${numero(resultado.errores)} with errors. ${numero(resultado.omitidas)} exports were not checked`,
            )}
            {resultado.motivosDeOmision.length
              ? ` (${resultado.motivosDeOmision.map(m => `${m.motivo}: ${numero(m.exportaciones)}`).join('; ')})`
              : ''}
            .
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-medium">
          {tr('Consultas y cambios', 'Checks and changes')}
          <NotaDeAyuda texto={A.consultas} />
        </h2>
        <div className="flex gap-1 text-sm">
          {(
            [
              ['pendientes', tr('Por revisar', 'To review')],
              ['cambios', tr('Con cambios', 'With changes')],
              ['todas', tr('Todas', 'All')],
            ] as const
          ).map(([clave, texto]) => (
            <button
              key={clave}
              type="button"
              onClick={() => setFiltro(clave)}
              className={`rounded-full border px-3 py-1 ${filtro === clave ? 'border-verde bg-verde-claro text-verde-oscuro' : 'border-linea bg-white text-apagado'}`}
            >
              {texto}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-2 space-y-2">
        {consultas.data?.consultas.length === 0 && (
          <p className="rounded-lg border border-linea bg-white p-4 text-sm text-apagado">
            {filtro === 'pendientes'
              ? tr('No hay cambios pendientes de revisar.', 'There are no changes pending review.')
              : tr('No hay consultas registradas.', 'No checks recorded.')}
          </p>
        )}
        {consultas.data?.consultas.map(c => (
          <Consulta
            key={c.id}
            consulta={c}
            onRevisada={permisos.editar ? () => void marcar.mutateAsync(c.id) : undefined}
          />
        ))}
      </div>
    </section>
  );
}

function Consulta({consulta, onRevisada}: {consulta: ConsultaDeNaviera; onRevisada?: () => void}) {
  const tono =
    consulta.resultado === 'updated'
      ? 'border-dorado/50'
      : consulta.resultado === 'error' || consulta.resultado === 'not_found'
        ? 'border-alerta/40'
        : 'border-linea';
  return (
    <div className={`rounded-lg border bg-white p-3 text-sm ${tono}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`/seguimiento/${encodeURIComponent(consulta.codigo)}`}
            className="font-medium text-verde hover:underline"
          >
            {consulta.codigo}
          </Link>
          <span className="text-apagado">{consulta.exportador ?? tr('De terceros', 'Third party')}</span>
          <span className="rounded-full bg-papel px-2 py-0.5 text-xs">{consulta.conectorEtiqueta}</span>
          <span className="text-xs text-apagado">booking {textoOGuion(consulta.booking)}</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-apagado">
          <span>{fechaHora(consulta.consultadaEl)}</span>
          <span className="font-medium text-tinta">{consulta.resultadoEtiqueta}</span>
          {consulta.resultado === 'updated' && !consulta.revisada && onRevisada && (
            <button
              type="button"
              onClick={onRevisada}
              className="rounded-md border border-linea px-2 py-1 hover:bg-papel"
            >
              {tr('✓ Marcar revisada', '✓ Mark as reviewed')}
            </button>
          )}
          {consulta.revisada && consulta.resultado === 'updated' && <span>{tr('Revisada', 'Reviewed')}</span>}
        </div>
      </div>
      {consulta.mensaje && <div className="mt-1 text-xs text-apagado">{consulta.mensaje}</div>}
      {consulta.cambios.length > 0 && (
        <ul className="mt-2 grid gap-x-6 gap-y-0.5 sm:grid-cols-2">
          {consulta.cambios.map(c => (
            <li key={c.campo} className="flex flex-wrap gap-1">
              <span className="text-apagado">{c.etiqueta}:</span>
              <span className="line-through decoration-alerta/60">
                {esFecha(c.antes) ? fecha(c.antes) : (c.antes ?? '—')}
              </span>
              <span aria-hidden>→</span>
              <span className="font-medium">{esFecha(c.despues) ? fecha(c.despues) : (c.despues ?? '—')}</span>
            </li>
          ))}
        </ul>
      )}
      {consulta.ultimoEvento && (
        <div className="mt-1 text-xs text-apagado">
          {tr(
            `Último evento real: ${consulta.ultimoEvento.tipo} en ${consulta.ultimoEvento.lugar ?? 'lugar sin nombre'} el ${fecha(consulta.ultimoEvento.fecha.slice(0, 10))}`,
            `Last actual event: ${consulta.ultimoEvento.tipo} at ${consulta.ultimoEvento.lugar ?? 'unnamed place'} on ${fecha(consulta.ultimoEvento.fecha.slice(0, 10))}`,
          )}
          {consulta.buque ? tr(` · buque ${consulta.buque}`, ` · vessel ${consulta.buque}`) : ''}
        </div>
      )}
    </div>
  );
}

const esFecha = (v: string | null) => !!v && /^\d{4}-\d{2}-\d{2}$/.test(v);

// "0 6 * * *" -> "6:00 a. m."
function horaDeCron(cron: string) {
  const partes = cron.trim().split(/\s+/);
  const minuto = Number(partes[0]);
  const hora = Number(partes[1]);
  if (!Number.isFinite(minuto) || !Number.isFinite(hora)) return cron;
  const sufijo = hora < 12 ? tr('a. m.', 'AM') : tr('p. m.', 'PM');
  const h12 = hora % 12 === 0 ? 12 : hora % 12;
  return `${h12}:${String(minuto).padStart(2, '0')} ${sufijo}`;
}
